import React, { useEffect, useRef, useState } from "react";
import MiraEmbed from "./MiraEmbed";
import { api, formatApiError } from "@/lib/api";

// v4 — "one free chat" demo gate (mirrors Club Concierge).
// Every visitor gets ONE complimentary Mira session. Engagement is detected
// via window blur → activeElement is the iframe (you can't see inside a
// cross-origin iframe). After 30 minutes, returning visitors see a branded
// lead-capture overlay instead of the chat. Soft enforcement (localStorage).
const SESSION_KEY = "mira_demo_session";
const SESSION_WINDOW_MS = 30 * 60 * 1000;

function readEngagedAt() {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.engaged_at === "number" ? parsed.engaged_at : null;
    } catch {
        return null;
    }
}

function GateOverlay({ source }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!name.trim() || !email.trim() || !phone.trim()) {
            setErr("Please share all three so our Concierge® desk can follow up.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/mira/leads", {
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim(),
                source,
            });
            setDone(true);
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mira-frame" data-testid="mira-gate-overlay">
            <div className="flex items-center justify-between px-1 pb-3">
                <span className="eyebrow" style={{ color: "hsl(var(--muted))" }}>
                    Mira · Your complimentary session
                </span>
                <span className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--muted))]">
                    <span
                        aria-hidden
                        className="inline-block w-[7px] h-[7px] rounded-full"
                        style={{ background: "hsl(var(--brass))" }}
                    />
                    Concierge® desk · Standing by
                </span>
            </div>

            <div
                className="border border-[hsl(var(--brass)/0.4)] p-7 md:p-10"
                style={{ background: "hsl(var(--navy))" }}
            >
                {done ? (
                    <div data-testid="mira-gate-success">
                        <p className="font-serif italic text-[22px] md:text-[26px] leading-snug text-[hsl(var(--paper))]">
                            You're on Mira's list.
                        </p>
                        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--paper)/0.75)]">
                            Our Concierge® desk will reach out shortly to open the door.
                        </p>
                    </div>
                ) : (
                    <>
                        <p className="font-serif italic text-[20px] md:text-[24px] leading-snug text-[hsl(var(--paper))]">
                            Your complimentary conversation with Mira has concluded.
                        </p>
                        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--paper)/0.75)]">
                            Mira becomes yours — included when your organisation joins Les Concierges®.
                            Leave your details and our Concierge® desk will open the door.
                        </p>

                        <form onSubmit={submit} className="mt-8 grid md:grid-cols-3 gap-5">
                            <div className="field field-on-dark">
                                <label htmlFor="mira-gate-name" className="text-[hsl(var(--paper)/0.6)]">Full name</label>
                                <input
                                    id="mira-gate-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Anjali Mehra"
                                    autoComplete="name"
                                    required
                                    data-testid="mira-gate-name"
                                />
                            </div>
                            <div className="field field-on-dark">
                                <label htmlFor="mira-gate-email" className="text-[hsl(var(--paper)/0.6)]">Work email</label>
                                <input
                                    id="mira-gate-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="anjali@yourcompany.com"
                                    autoComplete="email"
                                    required
                                    data-testid="mira-gate-email"
                                />
                            </div>
                            <div className="field field-on-dark">
                                <label htmlFor="mira-gate-phone" className="text-[hsl(var(--paper)/0.6)]">Phone</label>
                                <input
                                    id="mira-gate-phone"
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+91 97399 08844"
                                    autoComplete="tel"
                                    required
                                    data-testid="mira-gate-phone"
                                />
                            </div>

                            <div className="md:col-span-3 flex items-center justify-between gap-6 flex-wrap pt-2">
                                <p className="text-[12px] max-w-md leading-relaxed text-[hsl(var(--paper)/0.55)]">
                                    We use this only to follow up. No newsletters. No marketing lists.
                                    One concierge® will reach out — that is all.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60"
                                    style={{
                                        background: "hsl(var(--brass))",
                                        borderColor: "hsl(var(--brass))",
                                        color: "hsl(var(--navy))",
                                    }}
                                    data-testid="mira-gate-submit"
                                >
                                    {submitting ? "One moment…" : "Open the door →"}
                                </button>
                            </div>

                            {err && (
                                <p
                                    className="md:col-span-3 text-[13px] text-red-300"
                                    data-testid="mira-gate-error"
                                >
                                    {err}
                                </p>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

export default function MiraGate({ source = "homepage-demo" }) {
    const wrapRef = useRef(null);
    const [hydrated, setHydrated] = useState(false);
    const [gated, setGated] = useState(false);

    useEffect(() => {
        const engagedAt = readEngagedAt();
        setGated(Boolean(engagedAt) && Date.now() - engagedAt > SESSION_WINDOW_MS);
        setHydrated(true);
    }, []);

    useEffect(() => {
        if (gated) return undefined;
        const onBlur = () => {
            const el = document.activeElement;
            if (
                el &&
                el.tagName === "IFRAME" &&
                wrapRef.current &&
                wrapRef.current.contains(el) &&
                !readEngagedAt()
            ) {
                try {
                    localStorage.setItem(SESSION_KEY, JSON.stringify({ engaged_at: Date.now() }));
                } catch {
                    /* ignore storage errors */
                }
            }
        };
        window.addEventListener("blur", onBlur);
        return () => window.removeEventListener("blur", onBlur);
    }, [gated]);

    if (!hydrated) return null;
    if (gated) return <GateOverlay source={source} />;

    return (
        <div ref={wrapRef} data-testid="mira-gate-chat">
            <MiraEmbed />
        </div>
    );
}
